/**
 * A trade's life between "we agreed" and "it is in the log".
 *
 * The league already has a venue for negotiating — Discord — and this is not it. What the league
 * has never had is a CLOCK everybody can see. 2015-II-3 gives twenty hours then four; 2018-II-2
 * gives forty-eight in the offseason; 2017-II-3 replaces the clock with a position on the draft
 * board. Three different shapes of deadline, all of them counted by hand from a message
 * timestamp, in a channel where the message that starts the clock is not always the last one.
 *
 * So the rules live here and the screen renders them.
 *
 * THE ONE DECISION THIS FILE MAKES (Tim, 22 August). The clock starts at the AFFIRMATION — the
 * moment the other GM says yes inside the app — or, for a deal struck outside it, at the time a
 * commissioner records. Never at the filing time. Filing is one man's account of a conversation;
 * 2019-II-2 §2 says a trade is agreed only on "public declaration and confirmation", and the
 * confirmation is the half that moves. That single instant is what the header calls "agreed",
 * what the countdown counts from, and what the veto window is measured against — one timestamp
 * with two provenances, never two timestamps.
 *
 * The consequence worth stating: a trade filed and never affirmed has NO clock, and therefore
 * cannot expire. It sits in "awaiting" until it is affirmed or withdrawn. That is deliberate —
 * a window that started before the other party agreed would be counting down against a deal that
 * does not exist yet.
 */

import type {
  GmId, LiveTradeAsset, LiveTradeCounter, LiveTradeDoc, LiveTradeSide,
} from '../store/types.js';

/* -- the three shapes of window ---------------------------------------------------------------- */

/**
 * Which rule governs the clock. Derived from the date, not stored: a trade filed in July and
 * affirmed on draft morning is a draft-day trade, and storing the phase at filing time would
 * have got that wrong.
 */
export type WindowPhase = 'in-season' | 'offseason' | 'draft-day';

export const HOUR_MS = 3_600_000;

/** 2015-II-3: twenty hours of counters, then four for the original partner to match or beat. */
export const IN_SEASON_COUNTER_H = 20;
export const IN_SEASON_TOTAL_H = 24;
/** 2017-II-4 as extended by 2018-II-2 §1. Counters run the whole way. */
export const OFFSEASON_TOTAL_H = 48;
/** 2018-II-2 §1: the offseason window stops applying 48 hours before the draft starts. */
export const DRAFT_LOCKOUT_H = 48;
/**
 * How long after the draft's start the draft-day rules keep applying.
 *
 * 2018-II-2 §2 says "through the last pick of that year's draft", and the last pick is not a
 * time — the app has no way to know when a sixteen-round draft finished, and a slow one has run
 * past midnight. So the window is closed a day out, the same tail `preseasonOver` uses for the
 * rest of the app. Getting this wrong in the other direction was the bug this constant fixes:
 * with no upper bound at all, EVERY in-season trade for the rest of the year was governed by the
 * draft-day rules, because every October date is later than September's draft.
 */
export const DRAFT_TAIL_H = 24;
/** 2017-II-3 §2: announce a full round — ten picks — before the first pick involved. */
export const DRAFT_DAY_PICKS_AHEAD = 10;
/** 2017-II-3 §2: a first-round pick is announced by the clock instead, ten minutes out. */
export const FIRST_ROUND_MINUTES_AHEAD = 10;

/** 2016-I-3 §1: six of the eight GMs not in the trade, before the twenty-hour mark. */
export const VETOES_TO_KILL = 6;
export const UNINVOLVED_GMS = 8;
export const VETO_DEADLINE_H = IN_SEASON_COUNTER_H;

export interface WindowShape {
  phase: WindowPhase;
  /** Total hours the window runs. Null on draft day, where the window is measured in picks. */
  totalH: number | null;
  /** Hours during which a bystander may counter. Equal to `totalH` in the offseason. */
  counterH: number | null;
  /** What the strip says under the countdown: "of 48 — offseason". */
  label: string;
}

/**
 * Which window a trade affirmed at `at` falls under.
 *
 * The draft-day branch covers 2018-II-2 §2, which is wider than its name: the draft-day rules
 * apply from 48 hours before the first pick "through the last pick of that year's draft", not
 * only on the calendar day. A trade affirmed at breakfast on draft morning and one affirmed two
 * days earlier are governed identically, and both are counted in picks.
 */
export function windowShape(
  at: string | number | Date,
  { draftDate, inSeason = false }: { draftDate?: string | null; inSeason?: boolean },
): WindowShape {
  const t = new Date(at).getTime();
  const draft = draftDate ? Date.parse(draftDate) : NaN;
  const onTheBoard = Number.isFinite(draft)
    && t >= draft - DRAFT_LOCKOUT_H * HOUR_MS
    && t < draft + DRAFT_TAIL_H * HOUR_MS;
  if (onTheBoard) {
    return {
      phase: 'draft-day', totalH: null, counterH: null,
      label: 'no window — counters run until the pick is made',
    };
  }
  if (inSeason) {
    return {
      phase: 'in-season', totalH: IN_SEASON_TOTAL_H, counterH: IN_SEASON_COUNTER_H,
      label: `of ${IN_SEASON_TOTAL_H} — in season`,
    };
  }
  return {
    phase: 'offseason', totalH: OFFSEASON_TOTAL_H, counterH: OFFSEASON_TOTAL_H,
    label: `of ${OFFSEASON_TOTAL_H} — offseason`,
  };
}

/**
 * WHY THIS WINDOW, in the rulebook's own terms — what the `§` on a live trade opens.
 *
 * Tim, 3 September: "I'm also seeing that the trade window is 48 hours because these are
 * offseason trades? is that accurate? — if so, can you make sure when that's in play, the rules
 * popup outlines why." It is accurate, and the chain is three rules long, which is exactly why
 * a mark that opened only 2016-I-3 (vetoes) did not answer the question:
 *
 *   2015-II-3   the base clock — 20 hours of counters, then 4 for the original partner to match;
 *   2017-II-4   §1 extends the counter window to 48 hours "during the offseason", which §2
 *               defined as the championship game through preseason week 2;
 *   2018-II-2   §1 stretches that definition to 48 hours before the draft starts, and §2 hands
 *               the last 48 hours and the draft itself to 2017-II-3's pick-count rules.
 *
 * So a trade agreed in the week before the draft is an offseason trade, and its window is 48
 * hours — until the moment 48 hours before the first pick, when the shape changes under it. The
 * note says which of those applies to THIS trade and, where it matters, when the next shape
 * begins. `stampWhen` prints an instant the league's way; it is passed in so this file stays
 * free of Intl.
 */
export interface WindowWhy {
  title: string;
  note: string;
  rules: ReadonlyArray<{ id: string; section?: number; what: string }>;
}

export function windowWhy(
  agreedAt: string | number | Date,
  ctx: { draftDate?: string | null; inSeason?: boolean },
  stampWhen: (iso: string) => string = (iso) => iso,
): WindowWhy {
  const w = windowShape(agreedAt, ctx);
  const draft = ctx.draftDate ? Date.parse(ctx.draftDate) : NaN;
  const lockout = Number.isFinite(draft) ? new Date(draft - DRAFT_LOCKOUT_H * HOUR_MS).toISOString() : null;
  const vetoes = {
    id: '2016-I-3',
    what: 'Vetoes — six of the eight uninvolved GMs, declared before the 20-hour mark',
  };
  if (w.phase === 'offseason') {
    return {
      title: `Why this window is ${OFFSEASON_TOTAL_H} hours`,
      note: `This trade was agreed in the offseason. 2015-II-3 gives a trade 20 hours of counters and `
        + `4 to match; 2017-II-4 §1 extends the counter window to ${OFFSEASON_TOTAL_H} hours during the `
        + 'offseason, and 2018-II-2 §1 keeps that in force until 48 hours before the draft'
        + (lockout ? ` — ${stampWhen(lockout)}, for this draft` : '')
        + `. So counters run the full ${OFFSEASON_TOTAL_H} hours from the affirmation, and there is no `
        + `separate matching tail. Vetoes still close at ${VETO_DEADLINE_H} hours (2016-I-3).`,
      rules: [
        { id: '2017-II-4', section: 1, what: `The 48-hour offseason counter window` },
        { id: '2018-II-2', section: 1, what: 'Offseason runs until 48 hours before the draft' },
        { id: '2015-II-3', what: 'The 20 + 4 hour clock it extends' },
        vetoes,
      ],
    };
  }
  if (w.phase === 'in-season') {
    return {
      title: `Why this window is ${IN_SEASON_TOTAL_H} hours`,
      note: `This trade was agreed in season, so 2015-II-3 governs: counters for ${IN_SEASON_COUNTER_H} `
        + `hours from the affirmation, then ${IN_SEASON_TOTAL_H - IN_SEASON_COUNTER_H} hours in which `
        + 'only the original partner may match or beat an accepted counter. The 48-hour offseason '
        + 'window (2017-II-4, 2018-II-2) does not apply once the season has started.',
      rules: [
        { id: '2015-II-3', what: `${IN_SEASON_COUNTER_H} hours of counters, then ${IN_SEASON_TOTAL_H - IN_SEASON_COUNTER_H} to match` },
        { id: '2015-II-2', what: 'What a bystander may counter for' },
        vetoes,
      ],
    };
  }
  return {
    title: 'Why there is no clock on this trade',
    note: 'This trade was agreed inside the 48 hours before the draft, so 2018-II-2 §2 applies '
      + '2017-II-3: a trade that moves a current-season pick may be finalised the same day, must '
      + `be announced ${DRAFT_DAY_PICKS_AHEAD} picks before the first pick in it (${FIRST_ROUND_MINUTES_AHEAD} `
      + 'minutes before the draft for a first-rounder), and counters run until that pick is '
      + 'selected — even while its holder is on the clock. The hours-based windows do not apply.',
    rules: [
      { id: '2018-II-2', section: 2, what: 'The draft-day rules apply from 48 hours out' },
      { id: '2017-II-3', what: 'No clock — announce ten picks ahead, counters until the pick is made' },
      vetoes,
    ],
  };
}

/**
 * The one-line consequence shown under the submission form, before anything is sent.
 *
 * Written in the FUTURE tense and about the other GM, because that is the fact the filer keeps
 * getting wrong: he thinks the clock starts when he presses the button.
 */
export function windowPreview(
  at: string | number | Date,
  ctx: { draftDate?: string | null; inSeason?: boolean },
): string {
  const w = windowShape(at, ctx);
  if (w.phase === 'draft-day')
    return `No counter window — it must be announced ${DRAFT_DAY_PICKS_AHEAD} picks before the first pick in it.`;
  if (w.phase === 'in-season')
    return `Counters run ${IN_SEASON_COUNTER_H} hours from his affirmation, then ${
      IN_SEASON_TOTAL_H - IN_SEASON_COUNTER_H} for you to match — in season.`;
  return `Counters run ${OFFSEASON_TOTAL_H} hours from his affirmation — offseason.`;
}

/* -- the live record --------------------------------------------------------------------------- */

/*
 * THE RECORD SHAPES LIVE IN `store/types.ts` AND ARE ALIASED HERE.
 *
 * They were declared twice — once as the stored document and once as the domain's own idea of a
 * trade — and the two were kept in step by hand across four files, with `as never` at every call
 * site to make the compiler stop noticing. That is the arrangement that lets a field be added to
 * one and not the other, and the first symptom is a counter that silently loses its origin on the
 * way to the ledger. One declaration, aliased to the names this module reasons in.
 */
export type TradeAssetKind = LiveTradeAsset['kind'];
export type TradeAsset = LiveTradeAsset;
export type LiveSide = LiveTradeSide;
export type Counter = LiveTradeCounter;
export type LiveTrade = LiveTradeDoc;

export type TradeState =
  | 'awaiting' | 'open' | 'matching' | 'closed' | 'vetoed' | 'withdrawn' | 'replaced' | 'settled';

/**
 * Where a trade is right now.
 *
 * `matching` is the in-season tail — 2015-II-3's final four hours, when bystanders are done and
 * the original partner may match an accepted counter. It is a separate state rather than a flag
 * because the screen has to stop offering a bystander the composer at hour 20, not hour 24.
 */
export function tradeState(t: LiveTrade, ctx: WindowCtx): TradeState {
  const now = ctx.now ?? Date.now();
  if (t.withdrawn) return 'withdrawn';
  if (t.settledAs) return 'settled';
  // Replaced beats closed: a trade somebody countered out of never reaches the ledger, whatever
  // the clock says. It beats vetoed too — the veto killed the replacement, not this.
  if (t.replacedBy?.length) return 'replaced';
  if (!t.agreedAt) return 'awaiting';
  if (t.vetoes.length >= VETOES_TO_KILL) return 'vetoed';
  /*
   * A COMMISSIONER MAY END THE WAITING, AND THAT IS ALL HE MAY END (Tim, 8 September: "add
   * functionality as commish to push the trade through before the window is complete").
   *
   * Read after the veto check and never before it: pushing a trade through is closing the window
   * early, not overruling the league. Six declarations have already killed it by the time this
   * line is reached, and a commissioner who wants that undone has to take a veto off, in public,
   * where the card prints who did what.
   *
   * Read before the arithmetic, because the arithmetic is the thing being cut short. `closed` is
   * exactly what the clock would have produced on its own, so everything downstream — the
   * `awaitingFiling` test, `toFile`, the auto-file effect, `settlePlan` — happens the way it
   * always does. Nothing about settling knows this feature exists.
   */
  if (t.forced) return 'closed';
  const w = windowShape(t.agreedAt, ctx);
  /*
   * Draft day is not "no window" — it is a window measured in PICKS (2017-II-3 §2). It closes when
   * the first pick in the trade is selected, which is a board event and not a time, so the answer
   * comes from the board. Without a board — the page open at breakfast, nothing drafted yet — it
   * is open, which is correct: nothing has been selected.
   */
  if (w.totalH === null) {
    return draftWindowClosed(t, t.season, ctx) ? 'closed' : 'open';
  }
  const elapsed = now - Date.parse(t.agreedAt);
  if (elapsed >= w.totalH * HOUR_MS) return 'closed';
  if (w.counterH !== null && elapsed >= w.counterH * HOUR_MS) return 'matching';
  return 'open';
}

/** Milliseconds left in the window, or null where there is no clock. */
export function msLeft(t: LiveTrade, ctx: WindowCtx): number | null {
  if (!t.agreedAt) return null;
  const w = windowShape(t.agreedAt, ctx);
  if (w.totalH === null) return null;
  const left = Date.parse(t.agreedAt) + w.totalH * HOUR_MS - (ctx.now ?? Date.now());
  return Math.max(0, left);
}

/**
 * `31h 08m`. Two units, zero-padded minutes, and never seconds.
 *
 * Seconds on a 48-hour clock is theatre — it re-renders sixty times a minute to tell you
 * something that changed by 0.03%. The keeper deadline countdown made the same call.
 */
export function countdown(ms: number | null): string {
  if (ms === null) return '—';
  const mins = Math.floor(ms / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

/* -- who may see what -------------------------------------------------------------------------- */

/**
 * Before affirmation a trade is two men's private business (2022-trade-declarations §1: the
 * declaration is what makes it public, and affirming is what completes the declaration).
 *
 * The eight uninvolved GMs must not see it — not the names, not the assets, not that it exists.
 * Commissioners do, because they are the ones who have to affirm on somebody's behalf when a GM
 * is unreachable, and because a deal they cannot see is a deal they cannot police.
 */
export function canSeeTrade(t: LiveTrade, viewer: string, isCommissioner: boolean): boolean {
  if (t.agreedAt) return true;
  if (isCommissioner) return true;
  return parties(t).includes(viewer.toUpperCase());
}

export function parties(t: LiveTrade): string[] {
  return t.sides.map((s) => s.gm.toUpperCase());
}

export function isParty(t: LiveTrade, gm: string): boolean {
  return parties(t).includes(gm.toUpperCase());
}

/**
 * A counter is visible to the man it was sent to, the man who sent it, and nobody else —
 * 2015-II-2 makes them private and unlimited. A commissioner sees them read-only.
 *
 * The other PARTY to the trade does not see counters aimed at his counterparty. That is the
 * point of the rule: he finds out when one is accepted, in public, and gets his four hours.
 */
export function countersFor(t: LiveTrade, viewer: string, isCommissioner: boolean): Counter[] {
  const me = viewer.toUpperCase();
  if (isCommissioner) return t.counters;
  return t.counters.filter((c) => c.to.toUpperCase() === me || c.from.toUpperCase() === me);
}

/** Whether a bystander may still put an offer in. Never the two parties; never past the tail. */
export function canCounter(t: LiveTrade, viewer: string, ctx: WindowCtx): boolean {
  if (isParty(t, viewer)) return false;
  return tradeState(t, ctx) === 'open';
}

/* -- vetoes ------------------------------------------------------------------------------------- */

export interface VetoTally {
  declared: number;
  needed: number;
  /** Of the eight uninvolved GMs. Shown so "1 of 6" is not read as "1 of 6 GMs exist". */
  eligible: number;
  open: boolean;
  killed: boolean;
  /** The deadline is this app's reading of 2016-I-3, not its plain words. See `vetoTally`. */
  interpreted?: boolean;
}

/**
 * 2016-I-3 §1, and the deadline in it is the TWENTY-hour mark, not the end of the window.
 *
 * That matters in the offseason, where the window is 48: vetoes close at 20 and counters keep
 * running for another 28. Reading the rule as "before the window closes" would give the league
 * a veto power it has never had.
 */
export function vetoTally(t: LiveTrade, ctx: WindowCtx): VetoTally {
  const declared = new Set(t.vetoes.map((g) => g.toUpperCase()));
  for (const p of parties(t)) declared.delete(p);
  const now = ctx.now ?? Date.now();
  const started = t.agreedAt ? Date.parse(t.agreedAt) : null;
  /*
   * THE ONE PLACE THIS FILE INTERPRETS RATHER THAN APPLIES.
   *
   * 2016-I-3 closes vetoes at "the 20 hour trade window (pursuant to 2015-II-3)" — and 2017-II-3 §1
   * is the sentence that switches 2015-II-3 off for a draft-day trade. So on draft day the rulebook
   * gives the veto no deadline at all. Reading it literally leaves a trade vetoable after the pick
   * has been made and used, which cannot be what six-of-eight was for; reading it as closed leaves
   * a draft-day trade unvetoable, which cannot be either.
   *
   * So it follows the counter window, which is what the veto deadline has always tracked. Flagged
   * on the card as this app's reading, and on the list for the committee.
   */
  const onTheBoard = t.agreedAt !== null && t.agreedAt !== undefined
    && windowShape(t.agreedAt, ctx).phase === 'draft-day';
  const open = onTheBoard
    ? !draftWindowClosed(t, t.season, ctx)
    : started !== null && now - started < VETO_DEADLINE_H * HOUR_MS;
  return {
    declared: declared.size,
    /** True where the deadline is the app's reading of 2016-I-3 rather than its plain words. */
    interpreted: onTheBoard,
    needed: VETOES_TO_KILL,
    eligible: UNINVOLVED_GMS,
    open,
    killed: declared.size >= VETOES_TO_KILL,
  };
}

/* -- picks must balance ------------------------------------------------------------------------- */

/**
 * 2022-minimum-draft-picks §2: a GM must leave the draft having made one selection per round, so
 * any deal that moves picks has to return as many as it sends — per SEASON, since a 2027 pick
 * does not fill a 2026 round.
 *
 * Deliberately a WARNING, not a reassurance. Showing "picks balance ✓" on every trade that moves
 * none would train the eye to skip the line, which is the one place it has to be read.
 */
export interface Parity {
  season: number;
  /** Net picks for each side, index-matched to `t.sides`. */
  net: [number, number];
  /** The GM who would finish a round short. */
  short: GmId | null;
}

const SEASON_IN_PICK = /\b(20\d\d)\b/;

export function pickParity(sides: readonly [LiveSide, LiveSide]): Parity[] {
  const seasons = new Map<number, [number, number]>();
  sides.forEach((side, i) => {
    for (const a of side.sends) {
      if (a.kind !== 'pick') continue;
      const m = SEASON_IN_PICK.exec(a.text);
      if (!m) continue;
      const season = Number(m[1]);
      const row: [number, number] = seasons.get(season) ?? [0, 0];
      // A pick this side SENDS is a loss for him and a gain for the other.
      const mine = i === 0 ? 0 : 1;
      row[mine] -= 1;
      row[mine === 0 ? 1 : 0] += 1;
      seasons.set(season, row);
    }
  });
  return [...seasons.entries()]
    .map(([season, net]) => ({
      season,
      net,
      short: net[0]! < 0 ? sides[0]!.gm : net[1]! < 0 ? sides[1]!.gm : null,
    }))
    .filter((p) => p.short !== null)
    .sort((a, b) => a.season - b.season);
}

/** "2026 picks don't balance — Ryan would finish a round short." */
export function parityWarning(p: Parity, name: (gm: string) => string): string {
  return `${p.season} picks don’t balance — ${name(p.short!)} would finish a round short.`;
}

/* -- accepting a counter ------------------------------------------------------------------------ */

/**
 * A counter, accepted.
 *
 * 2015-II-2 in four sentences, and every one of them is load-bearing:
 *
 *   "If a GM accepts a counter-offer, he responds to the original thread saying he has accepted"
 *     — acceptance is PUBLIC, so the new trade is a trade like any other and appears to everybody.
 *   "The trade that goes through at the end of the trade window is the last one that the GM has
 *   accepted" — so the original is discontinued the moment one is accepted, not at the end.
 *   "A GM who accepts a counter offer cannot revert back" — so every other open offer to that same
 *     man is closed by the acceptance. He does not get to shop the acceptance around.
 *   "A GM that got bumped out of the trade can also make further counter offers" — so the ORIGINAL
 *     partner is not shut out; he counters into the new trade, inside the same window.
 *
 * THE WINDOW IS INHERITED, NOT RESTARTED, and that is the whole reason this returns two objects
 * rather than one. A fresh clock on every acceptance would let two GMs trade a deal back and forth
 * for a week without the league ever getting a settled 48 hours to veto in — which is exactly the
 * loophole 2016-I-3's deadline assumes cannot exist.
 */
export interface Acceptance {
  /** The original, discontinued and pointing at what replaced it. */
  original: LiveTrade;
  /** The new trade, inheriting the original's clock. */
  spawned: LiveTrade;
}

export function acceptCounter(
  t: LiveTrade, counterId: string, at: string, idFor: (c: Counter) => string,
): Acceptance | null {
  const c = t.counters.find((x) => x.id === counterId);
  if (!c || c.state !== 'open') return null;
  const accepting = c.to.toUpperCase();

  const original: LiveTrade = {
    ...t,
    counters: t.counters.map((x) => {
      if (x.id === c.id) return { ...x, state: 'accepted' as const };
      // Only the offers to THIS man close. The other party's negotiation is untouched — he may
      // still accept one of his own, and then both new trades stand.
      if (x.to.toUpperCase() === accepting && x.state === 'open') {
        return { ...x, state: 'declined' as const };
      }
      return x;
    }),
    replacedBy: [...(t.replacedBy ?? []), idFor(c)],
  };

  const spawned: LiveTrade = {
    id: idFor(c),
    season: t.season,
    // The counter's author is index 0, because he is the one who put the offer up.
    sides: [
      { gm: c.from, sends: c.sends },
      { gm: c.to, sends: c.receives },
    ],
    filedBy: c.from,
    filedAt: c.at,
    // Inherited, both the instant and where it came from: the new trade is inside the old window.
    ...(t.agreedAt ? { agreedAt: t.agreedAt } : {}),
    ...(t.agreedSource ? { agreedSource: t.agreedSource } : {}),
    ...(t.agreedBy ? { agreedBy: t.agreedBy } : {}),
    counters: [],
    // Vetoes do NOT carry: 2016-I-3 is a judgement about a specific trade, and the trade changed.
    vetoes: [],
    clauses: [],
    derivedFrom: t.id,
    accepted: { counterId: c.id, at },
  };

  return { original, spawned };
}

/** Declining closes that one offer and nothing else. It cannot be undone — the rule is one-way. */
export function declineCounter(t: LiveTrade, counterId: string): LiveTrade {
  return {
    ...t,
    counters: t.counters.map((x) => (x.id === counterId ? { ...x, state: 'declined' } : x)),
  };
}

/* -- settling into the ledger ------------------------------------------------------------------- */

/**
 * A trade that survived its window, in the shape the 109-row archive keeps.
 *
 * TWO CONVENTIONS MEET HERE and this function is the only place they are allowed to. A live trade
 * stores what each man SENDS, because that is what a man composing one chooses. The ledger stores
 * what each man GOT, because that is how it is announced and how eleven years of the sheet reads.
 * So the sides cross over, once, here — and `tests/tradeWindow.test.ts` asserts the crossing by
 * name rather than by index, because an off-by-one in this function silently rewrites history.
 *
 * The clauses become the note. They were never assets and they do not become assets on the way in.
 */
export function settlementFor(t: LiveTrade, recordedBy: GmId, at: string): {
  id: string;
  season: number;
  date: string;
  sides: [LedgerSide, LedgerSide];
  note?: string;
  recordedBy: GmId;
  recordedAt: string;
} {
  const got = (i: 0 | 1): LedgerSide => {
    const mine = t.sides[i];
    const theirs = t.sides[i === 0 ? 1 : 0];
    return {
      gm: mine.gm.toUpperCase(),
      players: theirs.sends.filter((a) => a.kind === 'player').map((a) => a.text),
      rights: theirs.sends.filter((a) => a.kind === 'rights').map((a) => a.text),
      picks: theirs.sends.filter((a) => a.kind === 'pick')
        // The origin travels with the pick, because "2026 9th" alone cannot say whose seat emptied
        // when a man holds two of them — the 19 November deal is exactly that case.
        .map((a) => (a.origin && a.origin.toUpperCase() !== theirs.gm.toUpperCase()
          ? `${a.text} (from ${a.origin})` : a.text)),
      faab: theirs.sends.filter((a) => a.kind === 'faab')
        .reduce((n, a) => n + (a.amount ?? 0), 0),
    };
  };
  const clauses = [
    ...t.clauses,
    ...t.sides.flatMap((s) => s.sends.filter((a) => a.kind === 'clause').map((a) => a.text)),
  ];
  return {
    // Derived from the live id, so settling twice writes the same row rather than two.
    id: `live-${t.id}`,
    season: t.season,
    /*
     * THE DATE IS THE AGREED INSTANT, not the day the window closed and not the day it was filed.
     * "A trade is dated when it was agreed" is the convention the whole archive is sorted by, and
     * a 48-hour window straddles a date boundary more often than not.
     */
    date: (t.agreedAt ?? t.filedAt).slice(0, 10),
    sides: [got(0), got(1)],
    ...(clauses.length ? { note: [...new Set(clauses)].join(' · ') } : {}),
    recordedBy,
    recordedAt: at,
  };
}

export interface LedgerSide {
  gm: string;
  players: string[];
  rights: string[];
  picks: string[];
  faab: number;
}

/**
 * Is this one waiting to be written into the log?
 *
 * Only a commissioner may write the ledger (`firestore.rules`, `match /trades`), so settling is an
 * act somebody performs rather than something that happens when a clock runs out. The card says so
 * — the parties see "closed, waiting to be filed" — because a trade that silently vanished from the
 * in-flight list and had not yet reached the archive would look like it had been lost.
 */
export function awaitingFiling(t: LiveTrade, ctx: WindowCtx): boolean {
  return tradeState(t, ctx) === 'closed' && !t.settledAs;
}

/**
 * Is this still worth a card at the top of the page?
 *
 * Everything in flight is, obviously. The judgement is about the ENDED ones, and it goes:
 *
 *   settled      — no. It is in the archive below, and the same trade in two states on one page is
 *                  a reader wondering which of them is true.
 *   closed       — yes, until a commissioner files it. Somebody has to do that, and the parties
 *                  have to be able to see that it has not been forgotten.
 *   vetoed,      — yes, but only while the window that produced them is still running. During the
 *   replaced       window they are the explanation for what everybody just watched happen; a week
 *                  later they are two dead cards above the log, and the survivor is in the archive.
 *   withdrawn    — no. Nobody but the filer ever agreed to it.
 */
export function stillListed(t: LiveTrade, ctx: WindowCtx): boolean {
  const state = tradeState(t, ctx);
  if (state === 'settled' || state === 'withdrawn') return false;
  if (state !== 'vetoed' && state !== 'replaced') return true;
  const left = t.agreedAt ? msLeftOf(t.agreedAt, ctx) : 0;
  return left > 0;
}

/** The window arithmetic on a bare instant, for a trade whose own state has already moved on. */
function msLeftOf(agreedAt: string, ctx: WindowCtx): number {
  const w = windowShape(agreedAt, ctx);
  if (w.totalH === null) return Infinity;
  return Math.max(0, Date.parse(agreedAt) + w.totalH * HOUR_MS - (ctx.now ?? Date.now()));
}

/* == DRAFT DAY ==================================================================================
 *
 * 2018-II-2 §2 pulls 2017-II-3 forward over the last 48 hours before the draft and holds it
 * "through the last pick of that year's fantasy football draft". Inside that period the trade
 * window stops being a duration and becomes a POSITION ON THE BOARD, and everything the rest of
 * this file does with milliseconds has to be done with pick numbers instead.
 *
 * Three separate rules, and the app was treating them as one "no window":
 *
 *   ELIGIBILITY (§1). A trade may be finalised the same day only if it moves at least one pick
 *     from the CURRENT season. A player-for-player deal struck on draft morning gets no such
 *     dispensation — it stays on the ordinary offseason clock, which will outlive the draft.
 *
 *   ANNOUNCEMENT (§2). It must be announced a full round — ten picks — before the FIRST pick in
 *     it. A first-round pick instead has to be announced ten minutes before the draft starts,
 *     because there is no "ten picks earlier" to be ten picks earlier than.
 *
 *   CLOSE (§2). Counters run "up until that draft pick has been selected", explicitly including
 *     while the GM holding it is on the clock. So the window ends on a SELECTION, not on a timer
 *     — §3 is the proof: when a pick clock expires the parties may still accept counters and the
 *     next GM simply drafts. A clock running out is not a pick being made.
 *
 * What the rulebook does NOT say is when a veto closes on draft day: 2016-I-3 pegs its deadline to
 * "the 20 hour trade window (pursuant to 2015-II-3)", and §1 of 2017-II-3 is the sentence that
 * switches 2015-II-3 off. This app closes vetoes when the counter window closes, on the reasoning
 * that the veto deadline has always been the counter deadline. That is an interpretation, it is
 * labelled as one on the card, and it is on the list for the committee.
 */

export interface DraftClock {
  /** Overall pick on the clock. Null before the board is live — nothing has been selected. */
  onTheClock: number | null;
  /** Round-one seat order. Needed to turn "2026 3rd" into an overall pick number. */
  order: readonly string[];
  teams: number;
  /** When the first pick is due. Only the first-round announcement rule needs it. */
  startsAt?: string | null;
}

export interface WindowCtx {
  draftDate?: string | null;
  inSeason?: boolean;
  now?: number;
  /** Present only during a draft. Without it a draft-day trade has no close it can compute. */
  clock?: DraftClock;
}

const ROUND_WORD = /\b(\d{1,2})\s*(?:st|nd|rd|th)\b/i;
const DOTTED = /\b(\d{1,2})\.(\d{1,2})\b/;

/** The round and, where the text carries one, the slot. */
export function pickCoords(text: string): { round: number; slot: number | null } | null {
  const dotted = DOTTED.exec(text);
  if (dotted) return { round: Number(dotted[1]), slot: Number(dotted[2]) };
  const ord = ROUND_WORD.exec(text);
  return ord ? { round: Number(ord[1]), slot: null } : null;
}

/**
 * Where a traded pick sits on the board.
 *
 * A pick written as a round alone — "2026 3rd", which is how the league writes them until the
 * lottery sets slots — is resolved through the SEAT it comes off, which is what `origin` is for.
 * Without an origin it is the sending GM's own seat, which is the right default and the reason
 * the origin selector only appears where a man holds two in a round.
 */
export function overallOf(a: TradeAsset, sender: string, clock: DraftClock): number | null {
  if (a.kind !== 'pick') return null;
  const c = pickCoords(a.text);
  if (!c) return null;
  /*
   * A DOTTED CODE HAS ALREADY SNAKED. `4.08` is the eighth pick made in round four — the number
   * the board prints and the room says — so it is overall 38 directly, the way `overallOfCode`
   * on the declaration screen reads it. Only a round-only pick needs the seat, and only the seat
   * needs the snake. Applying it to a code as well put every even-round dotted pick on the
   * wrong cell, which nothing noticed while trades were written as rounds.
   */
  if (c.slot !== null) return (c.round - 1) * clock.teams + c.slot;
  const seat = slotOfSeat(a.origin ?? sender, clock.order);
  if (seat === null) return null;
  return (c.round - 1) * clock.teams
    + (c.round % 2 === 1 ? seat : clock.teams - seat + 1);
}

function slotOfSeat(gm: string, order: readonly string[]): number | null {
  const i = order.findIndex((g) => g.toUpperCase() === gm.toUpperCase());
  return i < 0 ? null : i + 1;
}

/** 2017-II-3 §1: without a current-season pick there is no same-day finalisation. */
export function draftDayEligible(t: LiveTrade, season: number): boolean {
  return t.sides.some((s) => s.sends.some(
    (a) => a.kind === 'pick' && a.text.includes(String(season)),
  ));
}

export interface FirstPick {
  overall: number;
  round: number;
  /** The asset it came from, for naming it on the card. */
  label: string;
}

/** The earliest pick in the trade — the one the whole draft-day window is measured against. */
export function firstPickInvolved(
  t: LiveTrade, season: number, clock: DraftClock,
): FirstPick | null {
  const all: FirstPick[] = [];
  for (const side of t.sides) {
    for (const a of side.sends) {
      if (a.kind !== 'pick' || !a.text.includes(String(season))) continue;
      const overall = overallOf(a, side.gm, clock);
      const c = pickCoords(a.text);
      if (overall === null || !c) continue;
      all.push({ overall, round: c.round, label: a.text });
    }
  }
  if (!all.length) return null;
  return all.reduce((best, p) => (p.overall < best.overall ? p : best));
}

export interface Announcement {
  ok: boolean;
  /** Said in the card's own voice, whichever way it comes out. */
  why: string;
  /** The first pick it is measured against, when there is one. */
  first?: FirstPick;
}

/**
 * May this be announced now? 2017-II-3 §2, and the two halves are genuinely different tests.
 *
 * The first-round case is a CLOCK test because the board has not started: there is no tenth pick
 * before pick one to be ten picks earlier than, so the rule falls back to ten minutes of wall
 * time. Every other round is a POSITION test against the board as it stands.
 */
export function announcementCheck(
  t: LiveTrade, season: number, ctx: WindowCtx,
): Announcement {
  const clock = ctx.clock;
  if (!clock) return { ok: true, why: '' };
  if (!draftDayEligible(t, season)) {
    return {
      ok: false,
      why: `No ${season} pick in it, so it cannot be finalised during the draft (2017-II-3 §1). `
        + 'It keeps the ordinary window, which will outlast the draft.',
    };
  }
  const first = firstPickInvolved(t, season, clock);
  if (!first) {
    return { ok: true, why: 'The draft order is not set, so the announcement deadline cannot be checked yet.' };
  }
  if (first.round === 1) {
    const start = clock.startsAt ? Date.parse(clock.startsAt) : NaN;
    if (!Number.isFinite(start)) return { ok: true, why: '', first };
    const cutoff = start - FIRST_ROUND_MINUTES_AHEAD * 60_000;
    const ok = (ctx.now ?? Date.now()) <= cutoff;
    return {
      ok,
      first,
      why: ok
        ? `A first-round pick, so it must be announced ${FIRST_ROUND_MINUTES_AHEAD} minutes before `
          + 'the draft starts (2017-II-3 §2).'
        : `Too late — a trade with a first-round pick must be announced ${FIRST_ROUND_MINUTES_AHEAD} `
          + 'minutes before the draft starts (2017-II-3 §2).',
    };
  }
  const at = clock.onTheClock ?? 1;
  const latest = first.overall - DRAFT_DAY_PICKS_AHEAD;
  const ok = at <= latest;
  return {
    ok,
    first,
    why: ok
      ? `${first.label} is pick ${first.overall}, so it must be announced by pick ${latest} — `
        + `${latest - at + 1} to go (2017-II-3 §2).`
      : `Too late — ${first.label} is pick ${first.overall} and a full round, ${DRAFT_DAY_PICKS_AHEAD} `
        + `picks, had to pass first. The board is on ${at}.`,
  };
}

/**
 * How many picks until this window closes, or null where the question does not apply.
 *
 * Zero means the pick is on the clock — which is still OPEN, because §2 says counters run while
 * the holder is on the clock and §3 says an expired clock does not end them either. It closes when
 * the pick is SELECTED, which is `onTheClock` moving past it.
 */
export function picksUntilClose(
  t: LiveTrade, season: number, ctx: WindowCtx,
): number | null {
  const clock = ctx.clock;
  if (!clock || clock.onTheClock === null) return null;
  const first = firstPickInvolved(t, season, clock);
  if (!first) return null;
  return Math.max(0, first.overall - clock.onTheClock);
}

/** Has the pick this window hangs on been made? */
export function draftWindowClosed(t: LiveTrade, season: number, ctx: WindowCtx): boolean {
  const clock = ctx.clock;
  if (!clock || clock.onTheClock === null) return false;
  const first = firstPickInvolved(t, season, clock);
  if (!first) return false;
  return clock.onTheClock > first.overall;
}

/* == THE MATCHING TAIL ==========================================================================
 *
 * 2015-II-3's last sentence: "The final 4 hours (remaining from the 24 hour trade window) are set
 * aside for original trade partner to match or beat any accepted counter."
 *
 * WHAT "MATCH OR BEAT" MEANS HERE (Tim, 22 August). It is not a one-click re-offer of the winning
 * counter's terms. The bumped partner may compose a NEW trade freely — any assets he likes, on
 * either side — with one requirement: it must ask for **at least one asset from the other GM's
 * package in the original trade**. That is what makes it a match rather than an unrelated deal:
 * he is still trying to get the thing the trade was about, and giving the other man a reason to
 * deal with him instead of the GM whose counter was accepted.
 *
 * WHO GETS THE FOUR HOURS. The party who did NOT accept — the one bumped out. If both parties
 * accepted different counters, each is the bumped partner of the other's replacement, and each
 * gets the tail against it.
 *
 * WHY IT IS THE ONLY THING THAT MOVES IN THOSE FOUR HOURS. Bystanders are done at hour 20
 * (2015-II-3 reduces the counter window to 20); the tail belongs to one man. `canCounter` already
 * returns false for everybody once the state is `matching`, and this is the single exception.
 *
 * OFFSEASON HAS NO TAIL and needs none. 2018-II-2 lets counters run the whole 48 hours, and a
 * bumped partner is a bystander to the trade that replaced his — so he can simply counter it,
 * which 2015-II-2 says in as many words ("a GM that got bumped out of the trade can also make
 * further counter offers"). The app already allowed that; nothing here changes it.
 */

export interface MatchRight {
  /** May this viewer put a match in right now? */
  may: boolean;
  /** The other GM's original package — at least one of these must be asked for. */
  mustInclude: TradeAsset[];
  /** Everything he may ask for: the original package plus whatever is in the trade now. */
  mayAskFor: TradeAsset[];
  /** The man he is bidding against. */
  against: GmId | null;
  why: string;
}

const NO_RIGHT: MatchRight =
  { may: false, mustInclude: [], mayAskFor: [], against: null, why: '' };

/**
 * The bumped partner's standing against the trade that replaced his.
 *
 * `replacement` is the live trade on screen; `original` is the one it came out of, found through
 * `replacement.derivedFrom`. Without the original there is no package to require an asset from, so
 * there is no match right — which is correct rather than lenient: a trade nobody was bumped from
 * has no bumped partner.
 */
export function matchRight(
  replacement: LiveTrade,
  original: LiveTrade | undefined,
  viewer: string,
  ctx: WindowCtx,
): MatchRight {
  if (!original || replacement.derivedFrom !== original.id) return NO_RIGHT;
  const me = viewer.toUpperCase();
  if (!isParty(original, me) || isParty(replacement, me)) return NO_RIGHT;

  // The man who is in both: the one who accepted the counter, and the one being bid for.
  const staying = parties(original).find((g) => parties(replacement).includes(g));
  if (!staying) return NO_RIGHT;
  const hisPackage = original.sides.find((s) => s.gm.toUpperCase() === staying)?.sends ?? [];
  const nowOnOffer = replacement.sides.find((s) => s.gm.toUpperCase() === staying)?.sends ?? [];

  const state = tradeState(replacement, ctx);
  if (state !== 'matching') {
    return {
      ...NO_RIGHT,
      mustInclude: hisPackage,
      mayAskFor: dedupeAssets([...hisPackage, ...nowOnOffer]),
      against: staying,
      why: state === 'open'
        ? 'The window is still open to everybody — counter it like anybody else.'
        : 'The window has closed.',
    };
  }
  return {
    may: true,
    mustInclude: hisPackage,
    mayAskFor: dedupeAssets([...hisPackage, ...nowOnOffer]),
    against: staying,
    why: 'The last four hours are yours (2015-II-3). Compose what you like, as long as you ask for '
      + 'something that was in his original package.',
  };
}

/** Does this offer satisfy the one requirement? */
export function matchQualifies(
  receives: readonly TradeAsset[], mustInclude: readonly TradeAsset[],
): boolean {
  return receives.some((r) => mustInclude.some((m) => m.kind === r.kind && m.text === r.text));
}

function dedupeAssets(list: readonly TradeAsset[]): TradeAsset[] {
  const seen = new Set<string>();
  return list.filter((a) => {
    const k = `${a.kind}:${a.text}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
